import React, { useEffect, useState } from 'react';

import { campaign } from '../../constants';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../services/track.service';
import CustomDialog from '../Dialog/CustomDialog';
import type { Option } from '../onboarding/OnboardingOptionChipList';
import OnboardingStepBundesland from '../onboarding/OnboardingStepBundesland';
import OnboardingStepRoles from '../onboarding/OnboardingStepRoles';
import OnboardingStepSchule from '../onboarding/OnboardingStepSchule';
import OnboardingStepSchulfaecher from '../onboarding/OnboardingStepSchulfaecher';
import OnboardingStepSchulstufe from '../onboarding/OnboardingStepSchulstufe';
import OnboardingStepWichtigkeit from '../onboarding/OnboardingStepWichtigkeit';

export type ReOnboardingStep =
	| 'schulfaecher'
	| 'wichtigkeit'
	| 'roles'
	| 'schule'
	| 'bundesland'
	| 'schulstufe';

const SCHULFAECHER_SUBTITLE =
	'Select all matching subjects to get more relevant content and suggestions. ' +
	'You can change this later anytime.';

interface ReOnboardingDialogProps {
	open: boolean;
	steps: ReOnboardingStep[];
	onComplete: () => void;
	onClose: () => void;
	title?: string;
	subTitle?: string;
	dataTestId?: string;
}

const ReOnboardingDialog: React.FC<ReOnboardingDialogProps> = ({
	open,
	steps,
	onComplete,
	onClose,
	title,
	subTitle,
	dataTestId = 'reonboarding-dialog',
}) => {
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	// Bridge state: schulfaecher passes selected subjects to wichtigkeit for prioritization.
	// These two steps must appear in this order when both are included.
	const [selectedSubjects, setSelectedSubjects] = useState<Option[]>([]);

	const totalSteps = steps.length;

	useEffect(() => {
		if (open) {
			setCurrentStepIndex(0);
			setSelectedSubjects([]);
		}
	}, [open]);

	useEffect(() => {
		if (open) {
			ampliTrackMessageDisplayedEvent(
				campaign.reOnboardingDialog,
				MessageType.DIALOG,
				currentStepIndex + 1
			);
		}
	}, [open, currentStepIndex]);

	const goNext = () => {
		if (currentStepIndex < totalSteps - 1) {
			setCurrentStepIndex(currentStepIndex + 1);
		} else {
			onComplete();
		}
	};

	const goBack = currentStepIndex > 0 ? () => setCurrentStepIndex(currentStepIndex - 1) : null;

	if (!steps.length) return null;

	const stepNum = currentStepIndex + 1;
	const currentStep = steps[currentStepIndex] ?? steps[0];

	const commonProps = {
		setOnboardingStep: () => {},
		stepNum,
		totalSteps,
		onBackOverride: goBack,
		onNextOverride: goNext,
		hideImage: true,
		compact: true,
	};

	const stepComponents: Record<ReOnboardingStep, () => React.ReactElement> = {
		schulfaecher: () => (
			<OnboardingStepSchulfaecher
				{...commonProps}
				setInitialSelectedSubjects={setSelectedSubjects}
				subtitle={SCHULFAECHER_SUBTITLE}
			/>
		),
		wichtigkeit: () => (
			<OnboardingStepWichtigkeit {...commonProps} initialSelectedSubjects={selectedSubjects} />
		),
		roles: () => <OnboardingStepRoles {...commonProps} skipAttemptsIncrement />,
		schule: () => <OnboardingStepSchule {...commonProps} />,
		bundesland: () => <OnboardingStepBundesland {...commonProps} />,
		schulstufe: () => <OnboardingStepSchulstufe {...commonProps} />,
	};

	return (
		<CustomDialog
			dataTestId={dataTestId}
			open={open}
			onClose={onClose}
			title={title}
			subTitle={subTitle}
			dialogProps={{ maxWidth: 'sm' }}
			dialogTitleSx={{ paddingBottom: 0 }}
			dialogContentSx={{ overflow: 'hidden' }}
		>
			{stepComponents[currentStep]()}
		</CustomDialog>
	);
};

export default ReOnboardingDialog;
